import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearSession,
  connect,
  joinSession,
  onlineChanged,
  refreshSessionData,
  resume,
} from "../../actions/sessionActions";
import { loadHostProfile } from "../../actions/hostActions";
import { loadSets } from "../../actions/setsActions";
import { resumeSoundSync } from "../../actions/soundSyncActions";
import { clearSessionCache } from "../../offline/sessionCache";
import { clearFavoritesCache } from "../../offline/favoritesCache";
import { clearHostToken } from "../../session/hostTokens";
import { LYRIC_SIZES } from "../../lib/preferences";

/**
 * Owns the app-wide lifecycle: opens the real-time connection (which keeps reconnecting by
 * itself), keeps the current session joined and up to date across reconnects, and verifies
 * the connection whenever the page returns to the foreground or the network comes back.
 * Also applies the display preferences to the document. Renders nothing.
 */
const AppInit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const code = useSelector((state) => state.session.Code);
  const isConnected = useSelector((state) => state.session.isConnected);
  const notFound = useSelector((state) => state.session.notFound);
  const ended = useSelector((state) => state.session.ended);
  const hasSummary = useSelector((state) => Boolean(state.summary));
  const hasKey = useSelector((state) => state.host.hasKey);
  const hostLoaded = useSelector((state) => state.host.loaded);
  const { theme, highContrast, lyricSize, keepFavoritesOffline } = useSelector((state) => state.preferences);

  useEffect(() => {
    dispatch(connect());
    dispatch(loadHostProfile());

    let hiddenAt = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      dispatch(resume({ hiddenMs }));
      dispatch(resumeSoundSync()); // audio contexts are interrupted in the background on phones
    };
    const onOnline = () => dispatch(resume());
    const onOffline = () => dispatch(onlineChanged(false));
    // restored from the back/forward cache: the old connection is gone for sure
    const onPageShow = (event) => {
      if (event.persisted) dispatch(resume({ hiddenMs: Infinity }));
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [dispatch]);

  // Load the session as soon as we know its code, and again (plus re-join the real-time
  // group) every time the connection is (re-)established so missed updates are caught up.
  useEffect(() => {
    if (!code) return;
    dispatch(refreshSessionData({ force: true }));
    if (isConnected) dispatch(joinSession(code));
  }, [dispatch, code, isConnected]);

  // A host's sets follow the host key (and come back with the connection).
  useEffect(() => {
    if (hostLoaded) dispatch(loadSets());
  }, [dispatch, hostLoaded, hasKey, isConnected]);

  // Apply the display preferences to the document (see App.css for the tokens).
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "auto") delete root.dataset.theme;
    else root.dataset.theme = theme;
    if (highContrast) root.dataset.contrast = "high";
    else delete root.dataset.contrast;
    root.style.setProperty("--lyric-size", `${LYRIC_SIZES[lyricSize] || LYRIC_SIZES[1]}px`);
  }, [theme, highContrast, lyricSize]);

  useEffect(() => {
    if (!keepFavoritesOffline) clearFavoritesCache();
  }, [keepFavoritesOffline]);

  // The session no longer exists on the server (or the host ended it): move on, to the
  // summary when this device ended it, otherwise home, and only then forget the session.
  // Clearing it before the router has arrived would make the page being left notice the
  // missing session and send everyone home while the summary page is still on its way.
  useEffect(() => {
    if (!(notFound || ended)) return;
    const destination = ended && hasSummary ? "/ended" : "/";
    if (pathname !== destination) {
      navigate(destination, { replace: true });
      return;
    }
    clearHostToken(code);
    clearSessionCache(code);
    dispatch(clearSession());
  }, [dispatch, navigate, pathname, notFound, ended, hasSummary, code]);

  return null;
};

export default AppInit;
